import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Select,
  Typography,
  Tag,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import {
  getSuppliersStart,
  createSupplierStart,
  updateSupplierStart,
  deleteSupplierStart,
  clearCurrentSupplier,
} from "../../store/slices/supplierSlice";
import { Supplier } from "../../services/supplierService";
import PageHeader from "../../components/PageHeader";

const { Title } = Typography;
const { Option } = Select;

const Suppliers: React.FC = () => {
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  const { suppliers, loading, pagination } = useSelector(
    (state: RootState) => state.suppliers
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getSuppliersStart({ page: 1, limit: 10 }));
  }, [dispatch]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    dispatch(
      getSuppliersStart({
        page: 1,
        limit: 10,
        search: value,
      })
    );
  };

  const handleTableChange = (pagination: any) => {
    dispatch(
      getSuppliersStart({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
      })
    );
  };

  const showModal = (supplier?: Supplier) => {
    if (supplier) {
      setEditingSupplier(supplier);
      form.setFieldsValue({
        ...supplier,
        "address.street": supplier.address?.street,
        "address.city": supplier.address?.city,
        "address.state": supplier.address?.state,
        "address.zipCode": supplier.address?.zipCode,
        "address.country": supplier.address?.country,
      });
    } else {
      setEditingSupplier(null);
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingSupplier(null);
    form.resetFields();
    dispatch(clearCurrentSupplier());
  };

  const handleSubmit = async (values: any) => {
    const supplierData = {
      ...values,
      address: {
        street: values["address.street"],
        city: values["address.city"],
        state: values["address.state"],
        zipCode: values["address.zipCode"],
        country: values["address.country"],
      },
    };

    if (editingSupplier) {
      dispatch(
        updateSupplierStart({
          id: editingSupplier._id,
          data: supplierData,
        })
      );
    } else {
      dispatch(createSupplierStart(supplierData));
    }

    setIsModalVisible(false);
    form.resetFields();
    setEditingSupplier(null);
  };

  const handleDelete = (id: string) => {
    dispatch(deleteSupplierStart(id));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Payment Terms",
      dataIndex: "paymentTerms",
      key: "paymentTerms",
      render: (terms: string) => (
        <Tag color="blue">{terms.replace("_", " ").toUpperCase()}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: Supplier) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this supplier?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Suppliers"
        extra={
          <>
            <Input.Search
              placeholder="Search suppliers..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Add Supplier
            </Button>
          </>
        }
      />

      <div className="page-panel">
        <Table
          className="dense-table"
          size="small"
          columns={columns}
          dataSource={suppliers}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination?.currentPage || 1,
            total: pagination?.totalSuppliers || 0,
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} suppliers`,
          }}
          onChange={handleTableChange}
        />
      </div>

      <Modal
        title={editingSupplier ? "Edit Supplier" : "Add New Supplier"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                label="Supplier Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter supplier name" },
                ]}
              >
                <Input placeholder="Enter supplier name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Contact Person"
                name="contactPerson"
                rules={[
                  { required: true, message: "Please enter contact person" },
                ]}
              >
                <Input placeholder="Enter contact person" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Phone"
                name="phone"
                rules={[
                  { required: true, message: "Please enter phone number" },
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label="Payment Terms" name="paymentTerms">
                <Select placeholder="Select payment terms">
                  <Option value="net_30">Net 30</Option>
                  <Option value="net_60">Net 60</Option>
                  <Option value="cash_on_delivery">Cash on Delivery</Option>
                  <Option value="advance_payment">Advance Payment</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Credit Limit" name="creditLimit">
                <Input type="number" placeholder="Enter credit limit" />
              </Form.Item>
            </Col>
          </Row>

          <Title level={4}>Address</Title>
          <Row gutter={8}>
            <Col span={24}>
              <Form.Item label="Street" name="address.street">
                <Input placeholder="Enter street address" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={8}>
              <Form.Item label="City" name="address.city">
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="State" name="address.state">
                <Input placeholder="Enter state" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="ZIP Code" name="address.zipCode">
                <Input placeholder="Enter ZIP code" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label="Country" name="address.country">
                <Input placeholder="Enter country" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="State Code" name="stateCode">
                <Input placeholder="e.g. 27" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={8}>
              <Form.Item label="GSTIN" name="gstin">
                <Input placeholder="e.g. 27ERBPM6317J1ZK" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="PAN" name="pan">
                <Input placeholder="e.g. ERBPM6317J" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Primary DL No." name="drugLicenseNumber">
                <Input placeholder="Primary drug license" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Additional Drug Licenses"
            name="drugLicenseNumbers"
            tooltip="Enter multiple DL numbers (e.g. MH-SOL-417733, 21B-417734)"
          >
            <Select
              mode="tags"
              placeholder="Type DL and press Enter"
              tokenSeparators={[","]}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Row gutter={8}>
            <Col span={8}>
              <Form.Item label="Bank Name" name={["bankDetails", "bankName"]}>
                <Input placeholder="e.g. INDUSIND BANK" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Branch" name={["bankDetails", "branch"]}>
                <Input placeholder="Branch" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Account Number" name={["bankDetails", "accountNumber"]}>
                <Input placeholder="A/c number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label="IFSC" name={["bankDetails", "ifsc"]}>
                <Input placeholder="e.g. INDB0002292" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="UPI / PhonePe" name={["bankDetails", "upi"]}>
                <Input placeholder="UPI ID or phone" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} placeholder="Enter any notes" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingSupplier ? "Update" : "Create"} Supplier
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Suppliers;
